import * as React from 'react'

import { XIcon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  claimKeyboard,
  releaseKeyboard,
} from '~/client/src/shared/utils/keyboardClaim'

interface IProps {
  open: boolean
  title: string
  /** X / Escape / backdrop — back out without losing or saving the edits. */
  onKeepEditing: () => void
  onDiscard: () => void
  onSave: () => void
  discardTitle?: string
  saveTitle?: string
  portalContainer?: React.RefObject<HTMLElement | null>
}

/**
 * Three-outcome unsaved-changes confirmation (Keep editing / Don't save / Save) on the
 * shadcn Dialog primitive. Close (X) is top-left per the product convention, so shadcn's
 * default top-right X is suppressed.
 */
export const UnsavedChangesDialog: React.FC<IProps> = ({
  open,
  title,
  onKeepEditing,
  onDiscard,
  onSave,
  discardTitle,
  saveTitle,
  portalContainer,
}) => {
  // Stacks over its host, so it owns the window shortcuts too: Delete on a
  // focused footer button must not reach the markup editor it is asking about.
  React.useEffect(() => {
    if (!open) return
    const claim = claimKeyboard()
    return () => releaseKeyboard(claim)
  }, [open])

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) onKeepEditing()
      }}
    >
      <DialogContent
        className="tw:max-w-[420px]"
        showCloseButton={false}
        portalContainer={portalContainer}
      >
        <button
          type="button"
          onClick={onKeepEditing}
          aria-label={Localization.translator.close}
          className="tw:absolute tw:top-2 tw:left-2 tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-md tw:border-0 tw:bg-transparent tw:p-0 tw:text-muted-foreground tw:cursor-pointer tw:outline-none tw:transition-colors tw:hover:bg-muted tw:focus-visible:bg-muted"
        >
          <XIcon className="tw:size-4" aria-hidden="true" />
        </button>
        <DialogHeader className="tw:pl-8">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="lg" onClick={onDiscard}>
            {discardTitle ?? Localization.translator.dontSave}
          </Button>
          <Button variant="default" size="lg" onClick={onSave}>
            {saveTitle ?? Localization.translator.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
